import PreviewImageDialog from "@entrilia/shared/components/previewFile/PreviewImageDialog";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownwardRounded";
import type { Theme } from "@mui/material";
import { Box, Fab, Stack } from "@mui/material";
import type { SystemStyleObject } from "@mui/system";
import type { UIEvent } from "react";
import { useEffect, useRef, useState } from "react";
import type {
  AssistantAction,
  AssistantContentRef,
  AssistantFile,
  AssistantMappingChoices,
} from "../../../../api/types/assistantTypes";
import type { ChatItem } from "../chatState";
import ChatMessage from "./ChatMessage";

interface Props {
  items: ChatItem[];
  actionsDisabled: boolean;
  contentSx: SystemStyleObject<Theme>;
  openContent?: AssistantContentRef;
  onActionDecision: (action: AssistantAction, approve: boolean, mappingChoices?: AssistantMappingChoices) => void;
  onOpenFile: (file: AssistantFile) => void;
  onReply: (reply: string) => void;
  onToggleContent: (content: AssistantContentRef) => void;
}

const ChatHistory = ({
  items,
  actionsDisabled,
  contentSx,
  openContent,
  onActionDecision,
  onOpenFile,
  onReply,
  onToggleContent,
}: Props) => {
  const [imageDialogSrc, setImageDialogSrc] = useState("");
  const [showScrollBottomButton, setShowScrollBottomButton] = useState(false);

  const anchorElementRef = useRef<HTMLDivElement | null>(null);
  const isScrolledFromBottomRef = useRef(false);

  useEffect(() => {
    const scrollTimeoutId = setTimeout(() => {
      anchorElementRef.current?.scrollIntoView({ behavior: "smooth" });
    });
    return () => clearTimeout(scrollTimeoutId);
  }, [items.length]);

  useEffect(() => {
    if (isScrolledFromBottomRef.current) {
      return;
    }
    const scrollTimeoutId = setTimeout(() => {
      anchorElementRef.current?.scrollIntoView();
    });
    return () => clearTimeout(scrollTimeoutId);
  }, [items]);

  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    const { scrollHeight, scrollTop, clientHeight } = event.currentTarget;
    const isScrolledFromBottom = scrollHeight - scrollTop - clientHeight > 32;
    isScrolledFromBottomRef.current = isScrolledFromBottom;
    setShowScrollBottomButton(isScrolledFromBottom);
  };

  return (
    <Box sx={{ display: "flex", flex: 1, width: "100%", position: "relative" }}>
      <Box
        onScroll={handleScroll}
        sx={{
          display: "flex",
          position: "absolute",
          height: "100%",
          width: "100%",
          overflowY: "auto",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Stack spacing={2.5} useFlexGap sx={[contentSx, { marginTop: "auto", pt: 3, pb: 1 }]}>
          {items.map((item, index) => (
            <ChatMessage
              key={item.id}
              item={item}
              showHeader={items[index - 1]?.source !== "assistant"}
              actionsDisabled={actionsDisabled}
              showReplies={index === items.length - 1}
              openContent={openContent}
              onShowAsDialog={(imageSrc) => setImageDialogSrc(imageSrc)}
              onActionDecision={onActionDecision}
              onOpenFile={onOpenFile}
              onReply={onReply}
              onToggleContent={onToggleContent}
            />
          ))}
        </Stack>
        <Box
          ref={(r: HTMLDivElement) => {
            anchorElementRef.current = r;
          }}
        />
      </Box>
      {showScrollBottomButton && (
        <Fab
          size="small"
          sx={{ position: "absolute", left: "50%", bottom: 8, transform: "translateX(-50%)" }}
          onClick={() => anchorElementRef.current?.scrollIntoView({ behavior: "smooth" })}
        >
          <ArrowDownwardIcon />
        </Fab>
      )}
      <PreviewImageDialog src={imageDialogSrc} title="Image Preview" onClose={() => setImageDialogSrc("")} />
    </Box>
  );
};

export default ChatHistory;
